import { useEffect } from 'react';
import { arrayOf, shape, bool, func } from 'prop-types';
import styled from 'styled-components';
import filesize from 'filesize';
import Icon from '../../StoraEnsoComponents/Icon';
import { ReactComponent as ImageSvg } from '../../../assets/icons/photo-video.svg';

const progressBarColor = ({ theme, status }) =>
  ({
    running: `
      background-color: ${theme.colors.attention};
    `,
    failed: `
      background-color: ${theme.colors.negative};
    `,
    finished: `
      background-color: ${theme.colors.positive};
    `,
}[status]);

const ImageIcon = styled(ImageSvg)`
    width: 30px;
    height: 30px;
    fill: ${({theme}) => theme.colors.bg_gray_light};
    margin-bottom: 8px;
`;

const MissingPreviewWrapper = styled.div`
  position: relative;

  div {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    width: 100%;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: ${({theme}) => theme.colors.bg_gray_light};

    span {
      display: none;
    }

    @media screen and (min-width: 1200px) {
      span {
        display: block;
      }
    }
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 8px;
  row-gap: 8px;

  @media screen and (min-width: 576px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media screen and (min-width: 768px) {
    grid-template-columns: repeat(5, 1fr);
  }
`;

const Value = styled.span`
  display: block;
  font-size: 0.875rem;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ProgressBar = styled.div`
  width: ${({ total }) => `${total}%`};
  ${(props) => progressBarColor(props)};
  height: 5px;
  transition: width 800ms ease;
`;

const ProgressValue = styled.span`
  display: block;
  text-align: right;
  font-size: 12px;

  span.failed {
    color: ${({theme}) => theme.colors.negative};
  }
`;

const ThumbnailWrapper = styled.div`
  background-color: ${({theme}) => theme.colors.bg_gray_light};
  padding: 8px;

`;

const ImageWrapper = styled.div`
  position: relative;
  margin-bottom: 8px;

  img {
    width: 100%;
    height: auto;
    display: block;
  }
`;

const CloseIcon = styled(Icon)`
  position: absolute;
  top: 8px;
  right: 8px;
  color: ${({theme}) => theme.colors.white};
  cursor: pointer;
`;

function UploadPreview({ items, removeDisabled, onRemove }) {

  const getProgressLabel = ({status, loaded, size}) => {

    switch(status) {
      case 'running':
        return `${Math.round((100 * loaded) / size)}%`;
      case 'failed':
        return <span className={status}>Failed!</span>;
      case 'finished':
        return 'Uploaded';
    }
  }

  useEffect(() => {
    return () => items.forEach(item => URL.revokeObjectURL(item.preview));
  }, []);

  return (
    <Grid>
      {
        items.map((item, idx) => (
          <ThumbnailWrapper key={item.id}>
            <ImageWrapper>
              {
                !removeDisabled && (
                  <CloseIcon role="button" onClick={() => onRemove(idx)}>close</CloseIcon>
              )}
              {
                ['image/jpeg', 'image/png'].includes(item.file.type) ? (
                  <img src={item.preview} alt="Image" />
                ) : (
                  <MissingPreviewWrapper>
                    <img src={item.preview} alt="Image" />
                    <div>
                      <ImageIcon />
                      <span>No preview available</span>
                    </div>
                  </MissingPreviewWrapper>
                )
              }
            </ImageWrapper>
            <Value>{item.file.name}</Value>
            <Value>{ filesize(item.file.size, {round: 0}) }</Value>
            {
              item.uploadStatus && item.uploadStatus.status !== 'idle' && (
                <>
                  <ProgressBar status={item.uploadStatus.status} total={Math.round((100 * item.uploadStatus.loaded) / item.file.size)} />
                  <ProgressValue>{getProgressLabel({status: item.uploadStatus.status, loaded: item.uploadStatus.loaded, size: item.file.size})}</ProgressValue>
                </>
              )
            }
          </ThumbnailWrapper>
        ))
      }
    </Grid>
  );
}

UploadPreview.propTypes = {
  items: arrayOf(shape({})).isRequired,
  removeDisabled: bool,
  onRemove: func,
}

UploadPreview.defaultProps = {
  removeDisabled: false,
  onRemove: undefined,
}

export default UploadPreview;